import React, { useEffect, useMemo, useRef, useState } from "react";

// Searchable dropdown that mirrors a native <select> (onChange receives the value string).
// options: array of strings or { value, label } objects.
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  style,
  className,
  disabled = false,
  emptyText = "No matches",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const items = useMemo(
    () => options.map((o) => (typeof o === "object" ? o : { value: o, label: String(o) })),
    [options]
  );
  const selected = items.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((o) => o.label.toLowerCase().includes(q));
  }, [items, query]);

  useEffect(() => {
    function onDocClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (!open) setQuery("");
  }, [open]);

  const triggerStyle = {
    width: "100%", padding: "6px 8px", borderRadius: 7,
    border: "1.5px solid #d1d5db", background: "#fff", fontSize: 13,
    textAlign: "left", cursor: disabled ? "not-allowed" : "pointer",
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
    color: selected ? "#0f172a" : "#94a3b8", opacity: disabled ? 0.6 : 1,
    ...style,
  };

  function pick(v) {
    onChange(v);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className={className} style={{ position: "relative" }}>
      <button
        type="button"
        style={triggerStyle}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={{ fontSize: 10, color: "#94a3b8" }}>▾</span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute", zIndex: 50, top: "calc(100% + 4px)", left: 0, right: 0,
            background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
            boxShadow: "0 8px 24px rgba(15,23,42,0.14)", overflow: "hidden",
          }}
        >
          <div style={{ padding: 6, borderBottom: "1px solid #f1f5f9" }}>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              style={{
                width: "100%", padding: "6px 8px", borderRadius: 6,
                border: "1px solid #e2e8f0", fontSize: 13, outline: "none", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {placeholder && (
              <Row label={placeholder} active={!value} muted onClick={() => pick("")} />
            )}
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 12px", fontSize: 12, color: "#94a3b8" }}>{emptyText}</div>
            ) : (
              filtered.map((o) => (
                <Row key={o.value} label={o.label} active={o.value === value} onClick={() => pick(o.value)} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, active, muted, onClick }) {
  return (
    <div
      // onMouseDown + preventDefault: select deterministically before the search
      // input blurs / the list reflows, so the pick always registers and closes.
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      style={{
        padding: "8px 12px", fontSize: 13, cursor: "pointer",
        background: active ? "#eff6ff" : "transparent",
        color: muted ? "#94a3b8" : "#0f172a",
        fontWeight: active ? 700 : 400,
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "#f8fafc"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
    >
      {label}
    </div>
  );
}
